import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nContext';
import { busesAPI } from '../../services/api';
import PaginatedTable from '../../components/PaginatedTable';

function BusesManager() {
  const [buses, setBuses] = useState([]);
  const [form, setForm] = useState({ bus_number: '', license_plate: '', capacity: '', status: 'active' });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { t } = useI18n();

  const loadBuses = async () => {
    try {
      const { data } = await busesAPI.list();
      setBuses(data);
    } catch (err) { setError('Failed to load buses.'); }
  };

  useEffect(() => { loadBuses(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = { ...form, capacity: parseInt(form.capacity, 10) };
      if (editingId) {
        await busesAPI.update(editingId, payload);
      } else {
        await busesAPI.create(payload);
      }
      setForm({ bus_number: '', license_plate: '', capacity: '', status: 'active' });
      setEditingId(null);
      loadBuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed.');
    }
  };

  const handleEdit = (bus) => {
    setEditingId(bus.id);
    setForm({ bus_number: bus.bus_number, license_plate: bus.license_plate, capacity: bus.capacity, status: bus.status });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Soft delete this bus?')) return;
    try { await busesAPI.remove(id); loadBuses(); } catch (err) { setError('Delete failed.'); }
  };

  const columns = [
    { key: 'bus_number', label: t('busNumber') },
    { key: 'license_plate', label: t('licensePlate') },
    { key: 'capacity', label: t('capacity') },
    { key: 'status', label: t('status'), render: (v) => (
      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold', color: v === 'active' ? '#28a745' : v === 'maintenance' ? '#ffc107' : '#dc3545', backgroundColor: v === 'active' ? '#d4edda' : v === 'maintenance' ? '#fff3cd' : '#f8d7da' }}>{v}</span>
    )},
  ];

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <Link to="/admin" style={{ color: '#007bff', textDecoration: 'none' }}>{t('backToDashboard')}</Link>
      <h1>{t('manageBuses')}</h1>

      {error && <div style={{ background: '#fee', color: '#c00', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>{error}</div>}

      <form onSubmit={handleSubmit} style={styles.form}>
        <input placeholder={t('busNumberPlaceholder')} value={form.bus_number} onChange={(e) => setForm({...form, bus_number: e.target.value})} required style={styles.input} />
        <input placeholder={t('licensePlatePlaceholder')} value={form.license_plate} onChange={(e) => setForm({...form, license_plate: e.target.value})} required style={styles.input} />
        <input placeholder={t('capacityPlaceholder')} type="number" value={form.capacity} onChange={(e) => setForm({...form, capacity: e.target.value})} required style={styles.input} />
        <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} style={styles.input}>
          <option value="active">{t('active')}</option>
          <option value="maintenance">{t('maintenance')}</option>
          <option value="inactive">{t('inactive')}</option>
        </select>
        <button type="submit" style={styles.saveBtn}>{editingId ? t('update') : t('create')}</button>
        {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ bus_number: '', license_plate: '', capacity: '', status: 'active' }); }} style={styles.cancelBtn}>{t('cancel')}</button>}
      </form>

      <PaginatedTable data={buses} columns={columns} searchFields={['bus_number', 'license_plate', 'status']} onEdit={handleEdit} onDelete={handleDelete} />
    </div>
  );
}

const styles = {
  form: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' },
  input: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', minWidth: '150px' },
  saveBtn: { padding: '8px 16px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  cancelBtn: { padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
};

export default BusesManager;
